import dayjs from "dayjs"

import {
  FREIGHT_LINE_DEFAULT_QUANTITY,
  FREIGHT_LINE_UNIT_NAMES,
  SALES_ORDER_CANCELLABLE_STATUSES,
  SALES_ORDER_CONFIRMABLE_STATUSES,
  SALES_ORDER_EDITABLE_STATUSES,
  SALES_ORDER_STATUS_TO_LABEL,
  SALES_ORDER_STATUS_TO_TARGET_STATUSES,
  SHIPPED_DATE_VARIES,
  UOM_ID_TO_SYMBOL,
} from "./constants"
import { hasCpoDeliveryDateLineData } from "./cpoDeliveryDateLine"
import type {
  CpoDeliveryDateLineFormValues,
  CpoLineItemFormValues,
  CustomerPurchaseOrderFormValues,
  CustomerPurchaseOrderLineItemsFormValues,
  SalesOrderFreightLineFormValues,
  SalesOrderLineFormValues,
  SalesOrderLogisticsFormValues,
} from "./schemas"
import type { SelectedProduct } from "./types"

import { DateSpecialValue } from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import { DATE_FORMAT, UOM_CONVERSION_PRECISION } from "@/constants/formats"
import type {
  CustomerPurchaseOrderCreateInput,
  CustomerPurchaseOrderListItem,
  CustomerPurchaseOrderPricingTotalsInput,
  CustomerPurchaseOrderPricingTotalsProductTotal,
  CustomerPurchaseOrderPricingTotalsRootTotal,
  CustomerPurchaseOrderProduct,
  CustomerPurchaseOrderProductLine,
  CustomerPurchaseOrderProductLineUpsertInput,
  CustomerPurchaseOrderProductUpsertInput,
  SalesOrder,
  SalesOrderLineUpdateInput,
  SalesOrderStatus,
  SalesOrderUpdateInput,
  UnitOfMeasurement,
} from "@/graphql/codegen/graphql"
import { CompanyPreferredMeasurementSystemEnum, FreightTerms } from "@/graphql/codegen/graphql"
import type { CompanyProductAssociationListItem } from "@/screens/CustomerProducts/stores/useCustomerProductAssociationsStore"
import type { Option } from "@/types/form-fields"
import { convertFromTon, convertToTon, isTonUom, isUsUom } from "@/utils/conversion"
import { formatDate } from "@/utils/date"
import { resolveSpecialDate } from "@/utils/purchaseOrder"
import { isNil } from "@/utils/utils"

export interface LineItemOverTotals {
  totalSoPallets: number
  totalSoUnits: number
  totalUomAmount: number
  isPalletsOverTotal: boolean
  isUnitsOverTotal: boolean
  isUomOverTotal: boolean
}

export const calculateLineItemOverTotals = (
  lineItem: CpoLineItemFormValues,
  productTotal: CustomerPurchaseOrderPricingTotalsProductTotal | undefined
): LineItemOverTotals => {
  const totalSoPallets = productTotal?.pallets ?? 0
  const totalSoUnits = productTotal?.units ?? 0
  const totalUomAmount = productTotal?.uom?.amount ?? 0

  const isPalletsOverTotal = lineItem.pallets !== null && totalSoPallets > lineItem.pallets
  const isUnitsOverTotal = lineItem.units !== null && totalSoUnits > lineItem.units
  const isUomOverTotal = lineItem.quantity !== null && totalUomAmount > lineItem.quantity

  return { totalSoPallets, totalSoUnits, totalUomAmount, isPalletsOverTotal, isUnitsOverTotal, isUomOverTotal }
}

type LinkedQuantities = Pick<CpoDeliveryDateLineFormValues, "soPallets" | "soUnits" | "quantity">

export type LinkedQuantityField = keyof LinkedQuantities

export type PackageConversion = Pick<
  CpoLineItemFormValues,
  "packageUsWeight" | "packageMetricWeight" | "packageUsWeightUom" | "packageMetricWeightUom" | "packagesPerPallet"
>

const getWeightPerPackageInUom = (soUom: string | null, conversion: PackageConversion): number | null => {
  if (!soUom) return null

  const isUs = isUsUom(soUom)
  const weight = isUs ? conversion.packageUsWeight : conversion.packageMetricWeight
  const packageUom = isUs ? conversion.packageUsWeightUom : conversion.packageMetricWeightUom

  if (!weight || !packageUom) return null
  if (packageUom === soUom) return weight
  if (isTonUom(soUom) && !isTonUom(packageUom)) return convertToTon(weight, soUom)
  if (!isTonUom(soUom) && isTonUom(packageUom)) return convertFromTon(weight, packageUom)

  return null
}

export const calculateLinkedQuantities = ({
  editedField,
  values,
  soUom,
  conversion,
}: {
  editedField: LinkedQuantityField
  values: LinkedQuantities
  soUom: string | null
  conversion: PackageConversion
}): LinkedQuantities => {
  const edited = values[editedField]
  if (edited === null) return { soPallets: null, soUnits: null, quantity: null }

  const packagesPerPallet = conversion.packagesPerPallet || null
  const weightPerPackage = getWeightPerPackageInUom(soUom, conversion)

  let { soPallets, soUnits, quantity } = values

  if (editedField === "soPallets") {
    if (packagesPerPallet) soUnits = Number((edited * packagesPerPallet).toFixed(UOM_CONVERSION_PRECISION))
    if (weightPerPackage && packagesPerPallet)
      quantity = Number((edited * packagesPerPallet * weightPerPackage).toFixed(UOM_CONVERSION_PRECISION))
  } else if (editedField === "soUnits") {
    if (packagesPerPallet) soPallets = Number((edited / packagesPerPallet).toFixed(UOM_CONVERSION_PRECISION))
    if (weightPerPackage) quantity = Number((edited * weightPerPackage).toFixed(UOM_CONVERSION_PRECISION))
  } else if (weightPerPackage) {
    soUnits = Number((edited / weightPerPackage).toFixed(UOM_CONVERSION_PRECISION))
    if (packagesPerPallet) soPallets = Number((soUnits / packagesPerPallet).toFixed(UOM_CONVERSION_PRECISION))
  }

  return { soPallets, soUnits, quantity }
}

export const toCustomerPurchaseOrderPricingTotalsInput = (
  lineItems: CpoLineItemFormValues[],
  customerId: string
): CustomerPurchaseOrderPricingTotalsInput => ({
  customerId,
  products: lineItems.map((lineItem) => ({
    productId: lineItem.productId,
    uomId: lineItem.uom ?? undefined,
    quantity: lineItem.quantity ?? undefined,
    lines: lineItem.deliveryDateLines.map((line) => ({
      salesOrderId: line.salesOrderId ?? undefined,
      shipFromWarehouseAreaId: line.shipFromLocationId ?? undefined,
      effectiveDate: line.deliverByDate ?? undefined,
      units: line.soUnits ?? undefined,
      quantity: line.quantity ?? undefined,
      uomId: line.soUom ?? undefined,
      pallets: line.soPallets ?? undefined,
      pricePerUom: line.pricePerUom ?? undefined,
      priceUomId: line.isManualPriceSet ? (line.soUom ?? undefined) : undefined,
      isManualPriceSet: line.isManualPriceSet,
    })),
  })),
})

export const getCpoPricingTotalsTriggerKey = (lineItems: CpoLineItemFormValues[]): string =>
  JSON.stringify(
    lineItems.map((lineItem) => ({
      productId: lineItem.productId,
      uom: lineItem.uom,
      quantity: lineItem.quantity,
      lines: lineItem.deliveryDateLines.map((line) => ({
        shipFromLocationId: line.shipFromLocationId,
        soUom: line.soUom,
        quantity: line.quantity,
        pricePerUom: line.pricePerUom,
        isManualPriceSet: line.isManualPriceSet,
        salesOrderId: line.salesOrderId,
        deliverByDate: line.deliverByDate,
      })),
    }))
  )

export const toCustomerPurchaseOrderCreateInput = (
  formValues: CustomerPurchaseOrderFormValues,
  companyId: string,
  shipToLocationId: string,
  options?: { confirmDuplicate?: boolean }
): CustomerPurchaseOrderCreateInput => ({
  cpoNumber: formValues.cpoNumber.trim(),
  companyId,
  shipToLocationId,
  confirmDuplicate: options?.confirmDuplicate ?? false,
})

// Raw UoM ids (e.g. USSTD_WEIGHT_POUND) aren't meant for display; known weight units get their symbol,
// anything else falls back to the unit's name from the store.
export const formatUomSymbol = (uomId: string | null, units: Pick<UnitOfMeasurement, "id" | "name">[]): string => {
  if (!uomId) return "—"
  return UOM_ID_TO_SYMBOL[uomId] ?? units.find((unit) => unit.id === uomId)?.name ?? uomId
}

export const escapeIlikeWildcards = (query: string): string => query.replace(/[\\%_]/g, (match) => `\\${match}`)

export const deduplicateApprovedProducts = (
  items: CompanyProductAssociationListItem[]
): CompanyProductAssociationListItem[] => {
  const seenProductIds = new Set<number>()

  return items.filter((item) => {
    if (seenProductIds.has(item.productId)) return false
    seenProductIds.add(item.productId)
    return true
  })
}

type ChipProduct = Pick<
  NonNullable<CompanyProductAssociationListItem["product"]>,
  "publicId" | "companyProductDescription"
>

export const formatProductChipLabel = (product: ChipProduct | null | undefined): string => {
  const publicId = product?.publicId?.trim() || "Unknown ID"
  const description = product?.companyProductDescription?.trim() || "No description"
  return `${publicId} — ${description}`
}

// AK-5934 — seeds the line-items form from previously-saved products/lines when a CPO row
// expands. product.product resolves to the bare Product type, which lacks the per-company
// display fields (customerCode, customerDescription) that only CompanyProductAssociation carries
// — those come back null for reloaded lines until a follow-up adds that lookup.
export const toCustomerPurchaseOrderLineItemsFormValues = (
  products: CustomerPurchaseOrderProduct[],
  deliveryDateLineDefaults: CpoDeliveryDateLineDefaults
): CustomerPurchaseOrderLineItemsFormValues => ({
  lineItems: products.map((product) => toCpoLineItemFormValuesFromSavedProduct(product, deliveryDateLineDefaults)),
})

// A product with no saved lines (nothing touched before the last Save, so nothing was sent to
// persist) still needs a blank line to edit — same as a freshly-added product — so it falls back
// to the same default line toCpoLineItemFormValues seeds.
const toCpoLineItemFormValuesFromSavedProduct = (
  product: CustomerPurchaseOrderProduct,
  deliveryDateLineDefaults: CpoDeliveryDateLineDefaults
): CpoLineItemFormValues => {
  const cpoLineItemId = crypto.randomUUID()
  const packageWeightUoms: PackageWeightUoms = {
    packageUsWeightUom: product.product?.packageUsWeightUom ?? null,
    packageMetricWeightUom: product.product?.packageMetricWeightUom ?? null,
  }

  return {
    cpoLineItemId,
    customerPurchaseOrderProductId: product.id,
    cpoId: "",
    productId: product.productId,
    publicId: product.product?.publicId ?? null,
    externalSystemId: product.product?.externalSystemId ?? null,
    customerCode: null,
    customerDescription: null,
    companyProductDescription: product.product?.companyProductDescription ?? null,
    supplierName: product.product?.vendorDescription ?? null,
    computedProductStatus: product.product?.computedProductStatus ?? null,
    pallets: product.pallets ?? null,
    units: product.units ?? null,
    uom: product.uomId ?? null,
    quantity: product.quantityInUom ?? null,
    packageUsWeightUom: packageWeightUoms.packageUsWeightUom,
    packageMetricWeightUom: packageWeightUoms.packageMetricWeightUom,
    packageUsWeight: product.product?.packageUsWeight ?? null,
    packageMetricWeight: product.product?.packageMetricWeight ?? null,
    packagesPerPallet: product.product?.packagesPerPallet ?? null,
    noteCount: 0,
    deliveryDateLines: product.lines.length
      ? product.lines.map((line) => toCpoDeliveryDateLineFormValuesFromSavedLine(line, cpoLineItemId))
      : [getCpoDeliveryDateLineDefaultFormValues(cpoLineItemId, deliveryDateLineDefaults, packageWeightUoms)],
    createdAt: new Date().toISOString(),
  }
}

// isManualPriceSet has no real BE counterpart (no flag distinguishing manual vs. calculated price
// — pricing calculation itself doesn't exist yet) — treated as manual whenever a price is present
// so re-saving a reloaded line preserves it via priceUomId.
const toCpoDeliveryDateLineFormValuesFromSavedLine = (
  line: CustomerPurchaseOrderProductLine,
  cpoLineItemId: string
): CpoDeliveryDateLineFormValues => ({
  deliveryDateLineId: crypto.randomUUID(),
  customerPurchaseOrderProductLineId: line.id,
  cpoLineItemId,
  salesOrderId: line.salesOrderId ?? null,
  soNumber: line.salesOrder?.publicId ?? null,
  soStatus: line.salesOrder?.status ?? null,
  shipFromLocationId: line.shipFromWarehouseAreaId ?? null,
  defaultShipFromLocationId: line.shipFromWarehouseAreaId ?? null,
  shipByDate: line.isShipAsap ? DateSpecialValue.ASAP : (line.shippingDate ?? null),
  deliverByDate: line.deliverDate ?? null,
  isDeliverAsap: line.isDeliverAsap,
  soPallets: line.pallets ?? null,
  soUnits: line.units ?? null,
  quantity: line.quantityInUom ?? null,
  soUom: line.uomId,
  defaultSoUom: line.uomId,
  pricePerUom: line.pricePerUom ?? null,
  isManualPriceSet: line.pricePerUom !== null,
  createdAt: new Date().toISOString(),
})

interface CpoDeliveryDateLineDefaults {
  preferredShipFromLocationId: string | null
  preferredMeasurementSystem: CompanyPreferredMeasurementSystemEnum | undefined
}

interface PackageWeightUoms {
  packageUsWeightUom: string | null
  packageMetricWeightUom: string | null
}

// SO UoM defaults from the company's preferred measuring system, applied to this product's own
// US/Metric package weight UoMs — same pairing extractDefaultProductUom uses for PO's Quantity UoM.
const resolvePreferredSoUom = (
  preferredMeasurementSystem: CompanyPreferredMeasurementSystemEnum | undefined,
  { packageUsWeightUom, packageMetricWeightUom }: PackageWeightUoms
): string | null => {
  if (!preferredMeasurementSystem) return null
  return preferredMeasurementSystem === CompanyPreferredMeasurementSystemEnum.UsStandard
    ? packageUsWeightUom
    : packageMetricWeightUom
}

// the first delivery-date line auto-creates when a product is added
export const toCpoLineItemFormValues = (
  product: SelectedProduct,
  deliveryDateLineDefaults: CpoDeliveryDateLineDefaults
): CpoLineItemFormValues => {
  const cpoLineItemId = crypto.randomUUID()

  return {
    cpoLineItemId,
    customerPurchaseOrderProductId: null,
    cpoId: "",
    productId: product.productId,
    publicId: product.publicId,
    externalSystemId: product.externalSystemId,
    customerCode: product.customerCode,
    customerDescription: product.customerDescription,
    companyProductDescription: product.companyProductDescription,
    supplierName: product.supplierName,
    computedProductStatus: product.computedProductStatus,
    pallets: null,
    units: null,
    uom: null,
    quantity: null,
    packageUsWeightUom: product.packageUsWeightUom,
    packageMetricWeightUom: product.packageMetricWeightUom,
    packageUsWeight: product.packageUsWeight,
    packageMetricWeight: product.packageMetricWeight,
    packagesPerPallet: product.packagesPerPallet,
    noteCount: product.noteCount,
    deliveryDateLines: [getCpoDeliveryDateLineDefaultFormValues(cpoLineItemId, deliveryDateLineDefaults, product)],
    createdAt: new Date().toISOString(),
  }
}

export const getCpoDeliveryDateLineDefaultFormValues = (
  cpoLineItemId: string,
  defaults: CpoDeliveryDateLineDefaults,
  packageWeightUoms: PackageWeightUoms
): CpoDeliveryDateLineFormValues => {
  const preferredSoUom = resolvePreferredSoUom(defaults.preferredMeasurementSystem, packageWeightUoms)

  return {
    deliveryDateLineId: crypto.randomUUID(),
    customerPurchaseOrderProductLineId: null,
    cpoLineItemId,
    salesOrderId: null,
    soNumber: null,
    soStatus: null,
    shipFromLocationId: defaults.preferredShipFromLocationId,
    defaultShipFromLocationId: defaults.preferredShipFromLocationId,
    shipByDate: null,
    deliverByDate: null,
    isDeliverAsap: false,
    soPallets: null,
    soUnits: null,
    quantity: null,
    soUom: preferredSoUom,
    defaultSoUom: preferredSoUom,
    pricePerUom: null,
    isManualPriceSet: false,
    createdAt: new Date().toISOString(),
  }
}

// Target's non-nullable field (uomId) is guaranteed set by the time a touched line reaches here
// — cpoDeliveryDateLineFormSchema's refinements gate Save Changes on it. The `?? ""` fallback
// only satisfies TypeScript for the untouched/never-reached branch.
export const toCustomerPurchaseOrderProductLineUpsertInput = (
  deliveryDateLine: CpoDeliveryDateLineFormValues
): CustomerPurchaseOrderProductLineUpsertInput => ({
  id: deliveryDateLine.customerPurchaseOrderProductLineId ?? undefined,
  uomId: deliveryDateLine.soUom ?? "",
  // The BE stores ship ASAP as an empty date plus a flag, so the sentinel is stripped to null here.
  // Flags are written exactly as sent, so they are always sent explicitly.
  shippingDate: resolveSpecialDate(deliveryDateLine.shipByDate, DateSpecialValue.ASAP),
  isShipAsap: deliveryDateLine.shipByDate === DateSpecialValue.ASAP,
  deliverDate: deliveryDateLine.deliverByDate,
  isDeliverAsap: deliveryDateLine.isDeliverAsap,
  shipFromWarehouseAreaId: deliveryDateLine.shipFromLocationId,
  pallets: deliveryDateLine.soPallets,
  units: deliveryDateLine.soUnits,
  quantityInUom: deliveryDateLine.quantity,
  pricePerUom: deliveryDateLine.pricePerUom,
  priceUomId: deliveryDateLine.isManualPriceSet ? deliveryDateLine.soUom : undefined,
})

export const toCustomerPurchaseOrderProductUpsertInputs = (
  formValues: CustomerPurchaseOrderLineItemsFormValues
): CustomerPurchaseOrderProductUpsertInput[] =>
  formValues.lineItems.map((lineItem) => ({
    id: lineItem.customerPurchaseOrderProductId ?? undefined,
    productId: lineItem.productId,
    pallets: lineItem.pallets,
    units: lineItem.units,
    quantityInUom: lineItem.quantity,
    uomId: lineItem.uom,
    lines: lineItem.deliveryDateLines
      .filter(hasCpoDeliveryDateLineData)
      .map(toCustomerPurchaseOrderProductLineUpsertInput),
  }))

// Merges the real save response back into the current form state rather than replacing it
// wholesale — the response's CustomerPurchaseOrderProduct.product (bare Product) lacks the
// per-company display fields (customerCode, customerDescription, supplierName, etc.) already
// held locally from product selection, so only the newly-known ids and SO linkage are applied.
// Line matching relies on savedProduct.lines coming back in the same order they were sent (no
// explicit ORDER BY on the BE query) — acceptable for now, revisit if lines start reordering.
export const applyCustomerPurchaseOrderSaveResult = (
  formValues: CustomerPurchaseOrderLineItemsFormValues,
  savedProducts: CustomerPurchaseOrderProduct[]
): CustomerPurchaseOrderLineItemsFormValues => ({
  lineItems: formValues.lineItems.map((lineItem) => {
    const savedProduct = savedProducts.find((product) => product.productId === lineItem.productId)
    if (!savedProduct) return lineItem

    const savedLines = [...savedProduct.lines]

    return {
      ...lineItem,
      customerPurchaseOrderProductId: savedProduct.id,
      deliveryDateLines: lineItem.deliveryDateLines.map((deliveryDateLine) => {
        if (!hasCpoDeliveryDateLineData(deliveryDateLine)) return deliveryDateLine

        const savedLine = savedLines.shift()
        if (!savedLine) return deliveryDateLine

        return {
          ...deliveryDateLine,
          customerPurchaseOrderProductLineId: savedLine.id,
          salesOrderId: savedLine.salesOrderId ?? null,
          soNumber: savedLine.salesOrder?.publicId ?? null,
          soStatus: savedLine.salesOrder?.status ?? null,
        }
      }),
    }
  }),
})

export const toSalesOrderLineFormValues = (line: CustomerPurchaseOrderProductLine): SalesOrderLineFormValues => ({
  lineId: line.id,
  deliverDate: line.deliverDate ?? null,
  pallets: line.pallets ?? null,
  units: line.units ?? null,
  quantityInUom: line.quantityInUom ?? null,
  uomId: line.uomId ?? null,
  pricePerUom: line.pricePerUom ?? null,
  priceUomId: line.priceUomId ?? null,
  isManualPriceSet: line.isManualPriceSet ?? null,
})

export const toSalesOrderLineUpdateInput = (line: SalesOrderLineFormValues): SalesOrderLineUpdateInput => ({
  lineId: line.lineId,
  deliverDate: line.deliverDate,
  pallets: line.pallets,
  units: line.units,
  quantityInUom: line.quantityInUom,
  uomId: line.uomId,
  pricePerUom: line.pricePerUom,
  priceUomId: line.priceUomId,
  isManualPriceSet: line.isManualPriceSet,
})

const toFreightLineFormValues = (
  salesOrder: Pick<SalesOrder, "freightTerms" | "freightCost" | "freightQuantity" | "freightQuoteNumber">
): SalesOrderFreightLineFormValues | null => {
  if (salesOrder.freightTerms !== FreightTerms.PrepaidAndAdd) return null

  const defaults = createFreightLineFormValues(null)
  return {
    ...defaults,
    quantity: salesOrder.freightQuantity ?? defaults.quantity,
    pricePerUom: salesOrder.freightCost ?? null,
    quoteNumber: salesOrder.freightQuoteNumber ?? "",
  }
}

export const toSalesOrderLogisticsFormValues = (salesOrder: SalesOrder): SalesOrderLogisticsFormValues => ({
  shipFromWarehouseAreaId: salesOrder.shipFromWarehouseArea?.warehouseAreaId ?? null,
  deliveryDate: salesOrder.deliveryDate ?? null,
  isDeliveryDateAsap: salesOrder.isDeliveryDateAsap,
  ladingBill: salesOrder.ladingBill ?? "",
  confirmationSentOn: salesOrder.confirmationSentOn ?? null,
  promisedShipOn: salesOrder.promisedShipOn ?? null,
  requestedShipOn: salesOrder.isRequestedShipAsap ? DateSpecialValue.ASAP : (salesOrder.requestedShipOn ?? null),
  warehouseShippedOn: salesOrder.warehouseShippedOn ?? null,
  incoterms: salesOrder.incoterms ?? null,
  incotermsLocationId: salesOrder.incotermsLocationId ?? null,
  shipViaCompanyId: salesOrder.shipViaCompanyId ?? null,
  freightTerms: salesOrder.freightTerms ?? null,
  trackingNumber: salesOrder.trackingNumber ?? "",
  shipmentStatus: salesOrder.shipmentStatus ?? null,
  bolNotes: salesOrder.bolNotes ?? "",
  pickTicketNotes: salesOrder.pickTicketNotes ?? "",
  freightLine: toFreightLineFormValues(salesOrder),
  lines: salesOrder.lines.map(toSalesOrderLineFormValues),
})

// SalesOrderUpdateInput is a partial update where an omitted (undefined) field leaves the
// current value unchanged — so a cleared text field must be sent as null, never omitted,
// or the clear would silently fail to persist.
export const toSalesOrderUpdateInput = (
  formValues: SalesOrderLogisticsFormValues,
  salesOrderId: string
): SalesOrderUpdateInput => ({
  salesOrderId,
  shipFromWarehouseAreaId: formValues.shipFromWarehouseAreaId,
  ladingBill: formValues.ladingBill || null,
  confirmationSentOn: formValues.confirmationSentOn,
  promisedShipOn: formValues.promisedShipOn,
  // The BE stores ASAP as an empty date plus a flag, so the sentinel is stripped to null here.
  // Flags are written exactly as sent, so they are always sent explicitly.
  requestedShipOn: resolveSpecialDate(formValues.requestedShipOn, DateSpecialValue.ASAP),
  isRequestedShipAsap: formValues.requestedShipOn === DateSpecialValue.ASAP,
  isDeliveryDateAsap: formValues.isDeliveryDateAsap,
  warehouseShippedOn: formValues.warehouseShippedOn,
  incoterms: formValues.incoterms,
  incotermsLocationId: formValues.incotermsLocationId,
  shipViaCompanyId: formValues.shipViaCompanyId,
  freightTerms: formValues.freightTerms,
  // The BE accepts the freight quote only under Prepay & Add and clears it when the terms change,
  // so the fields are omitted (left unchanged) whenever there is no freight line.
  ...(formValues.freightLine && {
    freightCost: formValues.freightLine.pricePerUom,
    freightQuantity: formValues.freightLine.quantity,
    freightQuoteNumber: formValues.freightLine.quoteNumber.trim() || null,
  }),
  trackingNumber: formValues.trackingNumber || null,
  shipmentStatus: formValues.shipmentStatus,
  bolNotes: formValues.bolNotes || null,
  pickTicketNotes: formValues.pickTicketNotes || null,
  lines: formValues.lines.map(toSalesOrderLineUpdateInput),
})

export const calculateDeliveryDateFromShipDate = (requestedShipOn: string, transitDays: number): string | null => {
  const shipDate = dayjs(requestedShipOn)
  if (!shipDate.isValid()) return null
  return shipDate.add(transitDays, "day").format(DATE_FORMAT.ISO_18601)
}

export const calculateShipDateFromDeliveryDate = (deliveryDate: string, transitDays: number): string | null => {
  const deliverBy = dayjs(deliveryDate)
  if (!deliverBy.isValid()) return null
  return deliverBy.subtract(transitDays, "day").format(DATE_FORMAT.ISO_18601)
}

export const isSalesOrderEditable = (status: SalesOrderStatus): boolean =>
  SALES_ORDER_EDITABLE_STATUSES.includes(status)

export const isSalesOrderConfirmable = (status: SalesOrderStatus): boolean =>
  SALES_ORDER_CONFIRMABLE_STATUSES.includes(status)

export const isSalesOrderCancellable = (status: SalesOrderStatus): boolean =>
  SALES_ORDER_CANCELLABLE_STATUSES.includes(status)

export const isSalesOrderStatusChangeable = (status: SalesOrderStatus): boolean =>
  SALES_ORDER_STATUS_TO_TARGET_STATUSES[status].length > 0

// The current status is listed first so the select shows it as the initial value.
export const toSalesOrderStatusOptions = (status: SalesOrderStatus): Option<SalesOrderStatus>[] =>
  [status, ...SALES_ORDER_STATUS_TO_TARGET_STATUSES[status]].map((value) => ({
    value,
    label: SALES_ORDER_STATUS_TO_LABEL[value],
  }))

export const createFreightLineFormValues = (priceUomId: string | null): SalesOrderFreightLineFormValues => ({
  quantity: FREIGHT_LINE_DEFAULT_QUANTITY,
  pricePerUom: null,
  priceUomId,
  quoteNumber: "",
})

export const hasFreightLineData = (freightLine: SalesOrderFreightLineFormValues): boolean =>
  !isNil(freightLine.pricePerUom) || freightLine.quoteNumber.trim() !== ""

// Zero is a valid saved cost but does not count as a price, so only a value above zero satisfies confirmation.
export const isFreightPriceMissing = (freightLine: SalesOrderFreightLineFormValues | null): boolean =>
  !isNil(freightLine) && (freightLine.pricePerUom ?? 0) <= 0

export const isFreightQuantityMissing = (freightLine: SalesOrderFreightLineFormValues | null): boolean =>
  !isNil(freightLine) && isNil(freightLine.quantity)

export const findFreightUnit = (units: UnitOfMeasurement[]): UnitOfMeasurement | undefined =>
  units.find((unit) =>
    [unit.id, unit.name].some((value) => FREIGHT_LINE_UNIT_NAMES.includes(value?.trim().toLowerCase() ?? ""))
  )

export interface CpoHeaderTotals {
  pallets: number
  units: number
  quantityInUom: number | null
  uomId: string | null
  shippingDate: string | null
}

const EMPTY_CPO_HEADER_TOTALS: CpoHeaderTotals = {
  pallets: 0,
  units: 0,
  quantityInUom: null,
  uomId: null,
  shippingDate: null,
}

// Live totals only exist once the line items are loaded and priced, so until then the header keeps the
// aggregates the list item came with; once every line is removed there is nothing left to aggregate.
export const getCpoHeaderTotals = ({
  cpo,
  liveTotal,
  hasNoLineItems,
}: {
  cpo: Pick<CustomerPurchaseOrderListItem, "pallets" | "units" | "quantityInUom" | "uomId" | "shippingDate">
  liveTotal: CustomerPurchaseOrderPricingTotalsRootTotal | undefined
  hasNoLineItems: boolean
}): CpoHeaderTotals => {
  if (liveTotal) {
    return {
      pallets: liveTotal.pallets,
      units: liveTotal.units,
      quantityInUom: liveTotal.quantity,
      uomId: liveTotal.uomId ?? null,
      shippingDate: liveTotal.effectiveDate ?? null,
    }
  }

  if (hasNoLineItems) return EMPTY_CPO_HEADER_TOTALS

  return {
    pallets: cpo.pallets,
    units: cpo.units,
    quantityInUom: cpo.quantityInUom,
    uomId: cpo.uomId ?? null,
    shippingDate: cpo.shippingDate ?? null,
  }
}

export const formatShippedDate = (shippingDate: string | null): string => {
  if (!shippingDate) return "—"
  if (shippingDate === SHIPPED_DATE_VARIES) return shippingDate

  return formatDate(shippingDate)
}
